import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import CustomBadge from "shared/components/common/customBadge";
import CustomButton from "shared/components/common/customButton";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import TransactionDetailModal, {
  TransactionData,
  TransactionStatus,
} from "shared/modal/transactionDetailModal";
import {
  GetAllTransactionsService,
  TransferPaymentService,
} from "shared/services/transactions";
import styles from "./style.module.scss";

const DashboardTransactions = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [viewModal, setViewModal] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: string | null;
    transaction: TransactionData | null;
  }>({
    open: false,
    action: null,
    transaction: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<TransactionData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionData | null>(null);

  const openViewModal = (transaction: TransactionData) => {
    setSelectedTransaction(transaction);
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewModal(false);
    setSelectedTransaction(null);
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, action: null, transaction: null });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handleGetAllTransactions(page);
  };

  const tableHeads = [
    { title: "Customer", key: "customer" },
    { title: "Supplier", key: "supplier" },
    { title: "Amount", key: "amount" },
    { title: "Installment", key: "installment" },
    { title: "Date & Time", key: "dateTime" },
    { title: "Payment Status", key: "paymentStatus" },
    { title: "Released", key: "released" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllTransactions = async (page = 1) => {
    try {
      setLoading(true);
      const response = await GetAllTransactionsService(page, 10);
      const transactionData = Array.isArray(response.data.data.data)
        ? response.data.data.data
        : [];
      setTransactions(transactionData);
      setTotalItems(response.data.data.meta.totalItems || 0);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch transactions. Please try again.";
      toastMessage("error", errorMessage);
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetAllTransactions();
    // eslint-disable-next-line
  }, []);

  const handleReleasePayment = (transaction: TransactionData) => {
    setConfirmModal({
      open: true,
      action: "Release Payment",
      transaction,
    });
  };

  const confirmReleasePayment = async () => {
    if (!confirmModal.transaction) return;

    setActionLoading(true);
    try {
      const transactionId = confirmModal.transaction.id;
      await TransferPaymentService(transactionId);
      toastMessage("success", "Payment released successfully!");

      setTransactions((prevTransactions) =>
        prevTransactions.map((t) =>
          t.id === transactionId ? { ...t, is_released: 1 } : t
        )
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        "Failed to release payment. Please try again.";
      toastMessage("error", errorMessage);
      console.error("Error releasing payment:", error);
    } finally {
      setActionLoading(false);
      closeConfirmModal();
    }
  };

  const getPaymentStatusBadge = (status: TransactionStatus) => {
    switch (status) {
      case TransactionStatus.Paid:
        return { text: "Paid", textColor: "#027A48", bg: "#ecfdf3" };
      case TransactionStatus.Pending:
        return { text: "Pending", textColor: "#B54708", bg: "#fffaeb" };
      default:
        return { text: "Unknown", textColor: "#667085", bg: "#f2f4f7" };
    }
  };

  const getReleasedBadge = (isReleased: number) => {
    return isReleased === 0
      ? { text: "Released", textColor: "#027A48", bg: "#ecfdf3" }
      : { text: "Pending Release", textColor: "#ED1C24", bg: "#ed1c240f" };
  };

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={tableHeads}
          title="Transaction List"
          isEmpty={!transactions.length}
          loading={loading}
        >
          {loading
            ? Array.from({ length: 5 }).map((_, index) => (
              <TableRowLoader key={index} />
            ))
            : transactions.map((transaction) => {
              const paymentStatus = getPaymentStatusBadge(transaction.status);
              const releasedStatus = getReleasedBadge(transaction.is_released);
              const isNotReleased = transaction.is_released === 1;

              return (
                <tr key={transaction.id}>
                  <td>
                    <div className={styles.userCell}>
                      <span className={styles.userName}>
                        {transaction.user?.fullname || "-"}
                      </span>
                      <span className={styles.userEmail}>
                        {transaction.user?.email || "-"}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.userCell}>
                      <span className={styles.userName}>
                        {transaction.receiver?.user?.fullname || "-"}
                      </span>
                      <span className={styles.userEmail}>
                        {transaction.receiver?.user?.email || "-"}
                      </span>
                    </div>
                  </td>
                  <td className={styles.amountCell}>${transaction.amount}</td>
                  <td>
                    {transaction.current_installment} /{" "}
                    {transaction.job?.payment_installments || 1}
                  </td>
                  <td>{new Date(transaction.created_at).toLocaleString()}</td>
                  <td>
                    <CustomBadge
                      textColor={paymentStatus.textColor}
                      bg={paymentStatus.bg}
                      title={paymentStatus.text}
                    />
                  </td>
                  <td>
                    <CustomBadge
                      textColor={releasedStatus.textColor}
                      bg={releasedStatus.bg}
                      title={releasedStatus.text}
                    />
                  </td>
                  <td className={classNames(styles.IconsContainer)}>
                    {isNotReleased && (
                      <CustomToolTip label="Release Payment">
                        <CustomButton
                          containerStyle="py-2 px-3 text-xs font-normal"
                          title="Release"
                          onClick={() => handleReleasePayment(transaction)}
                        />
                      </CustomToolTip>
                    )}

                    <CustomToolTip label="View Details">
                      <button onClick={() => openViewModal(transaction)}>
                        <Icons.Eye />
                      </button>
                    </CustomToolTip>
                  </td>
                </tr>
              );
            })}
        </CustomTable>
      </div>

      <div className={classNames(styles.paginationContainer, "mt-3")}>
        <CustomPagination
          onPageChange={handlePageChange}
          totalCount={totalItems}
          currentPage={currentPage}
          pageSize={10}
        />
      </div>

      {selectedTransaction && (
        <TransactionDetailModal
          show={viewModal}
          handleClose={closeViewModal}
          transaction={selectedTransaction}
        />
      )}

      {confirmModal.open && (
        <ConfirmationModal
          show={confirmModal.open}
          loading={actionLoading}
          title={`Confirm ${confirmModal.action}`}
          actionButtonText={`Yes, ${confirmModal.action}`}
          subtitle={`Are you sure you want to release this payment of $${confirmModal.transaction?.amount} to ${confirmModal.transaction?.receiver?.user?.fullname}?`}
          handleSubmit={confirmReleasePayment}
          handleClose={closeConfirmModal}
        />
      )}
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 8 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default DashboardTransactions;
